// CTR Optimizer — which listings lose clicks at the search result, and why.
//
// Three surfaces, matching the three questions a seller has and the order they
// have them: which product has a problem, why, and did my fix work. Six
// components render as three surfaces rather than six, or the same data
// appears in several places with nothing tying it together.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiLink, FiLock } from 'react-icons/fi';

import shell from '../SellerIntelligence.module.css';
import s from './CTROptimizer.module.css';
import AuthContext from '../../../context/AuthContext';
import { useAmazonStatus } from '../../../hooks/useAmazonStatus';
import FeatureCards from './components/FeatureCards';
import ProductList from './components/ProductList';
import ProductDetail from './components/ProductDetail';
import Changes from './components/Changes';
import { NotConnectedError, getChanges, getOverview, getProduct,
         getProducts } from './api';
import { num } from './format';

const TABS = [
  { id: 'rate', label: 'Products losing clicks', ref: 'SF1 · SF5' },
  { id: 'changes',  ref: 'SF2 · SF3 · SF4' },
];

export default function CTROptimizer() {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const { connected, loading: checking } = useAmazonStatus(token);

  const [tab, setTab] = useState('rate');
  const [period, setPeriod] = useState('WEEK');
  const [startDate, setStartDate] = useState('');
  const [windowOptions, setWindowOptions] = useState([]);

  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState(null);
  const [changes, setChanges] = useState(null);
  const [openAsin, setOpenAsin] = useState(null);
  const [rollUp, setRollUp] = useState(false);
  const [detail, setDetail] = useState(null);

  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const fail = useCallback((e) => {
    if (e instanceof NotConnectedError) setNotConnected(true);
    else setError(e.message);
  }, []);

  useEffect(() => {
    if (!token || !connected) return undefined;
    let live = true;
    getOverview(token, { period, start_date: startDate })
      .then((r) => {
        if (!live) return;
        setOverview(r);
        setWindowOptions(r.window_options || []);
        setLocked(!!r.locked);
        if (r.start_date && !startDate) setStartDate(r.start_date);
      })
      .catch((e) => { if (live) fail(e); });
    return () => { live = false; };
  }, [token, connected, period, startDate, fail]);

  const load = useCallback(async () => {
    if (!token || !connected) return;
    setLoading(true);
    setError(null);
    try {
      const args = { period, start_date: startDate };
      if (tab === 'rate') setProducts(await getProducts(token,
        { ...args, roll_up: rollUp || undefined }));
      else setChanges(await getChanges(token, args));
    } catch (e) { fail(e); } finally { setLoading(false); }
  }, [token, connected, tab, period, startDate, rollUp, fail]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openAsin || !token) { setDetail(null); return; }
    getProduct(token, openAsin, { period, start_date: startDate })
      .then((r) => setDetail(r.product)).catch(fail);
  }, [openAsin, token, period, startDate, fail]);

  function body() {
    if (checking) return <div className={s.empty}>Checking connection…</div>;
    if (!connected || notConnected) {
      return (
        <div className={s.locked}>
          <div className={s.lockedIcon}><FiLink /></div>
          <h2 className={s.lockedTitle}>Connect Amazon</h2>
          <p className={s.lockedBody}>Search impressions come from your Amazon reports.</p>
          <button className={s.button} onClick={() => navigate('/dashboard/market-places')}>
            Connect Amazon
          </button>
        </div>
      );
    }

    return (
      <div className={s.page}>
        {error && <div className={s.error}>{error}</div>}

        <section className={s.panel}>
          <div className={s.contextBar}>
            <div className={s.contextLeft}>
              <label className={s.field}>
                <span className={s.label}>Period</span>
                <select className={s.select} value={period}
                        onChange={(e) => { setPeriod(e.target.value); setStartDate(''); }}>
                  {['WEEK', 'MONTH', 'QUARTER'].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className={s.field}>
                <span className={s.label}>Window</span>
                <select className={s.select} value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}>
                  {!windowOptions.length && <option value="">No data yet</option>}
                  {windowOptions.map((w) => (
                    <option key={w.start_date} value={w.start_date}>
                      {w.start_date}{w.collected ? '' : ' — not collected'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {overview?.coverage && (
              <div className={overview.coverage.complete ? s.coverage : s.coverageWarn}>
                {num(overview.coverage.products_with_data)} of{' '}
                {num(overview.coverage.products_in_catalogue)} products have search data
                behind these figures
              </div>
            )}
          </div>
        </section>

        <FeatureCards data={overview} loading={loading && !overview}
                      onOpen={(next) => setTab(next === 'why' ? 'rate' : next)} />

        <section className={s.panel}>
          <div className={s.tabs}>
            {TABS.map((t) => (
              <button key={t.id} className={tab === t.id ? s.tabActive : s.tab}
                      onClick={() => setTab(t.id)}>
                {t.label}<span className={s.tabRef}>{t.ref}</span>
              </button>
            ))}
          </div>

          {locked ? (
            <div className={s.locked}>
              <div className={s.lockedIcon}><FiLock /></div>
              <h2 className={s.lockedTitle}>Search click rate is not available yet</h2>
              <p className={s.lockedBody}>
                Search impressions come from a Brand Analytics report. Amazon serves it to
                the owner of a brand, so without that this feature has no impressions to
                measure a click rate against.
              </p>
            </div>
          ) : (
            <div style={{ paddingTop: 16 }}>
              {tab === 'rate' && <ProductList data={products} onOpen={setOpenAsin}
                                              rollUp={rollUp} onRollUp={setRollUp} />}
              {tab === 'changes' && <Changes data={changes} />}
            </div>
          )}
        </section>

        {openAsin && detail && (
          <ProductDetail data={detail} onClose={() => setOpenAsin(null)} />
        )}
      </div>
    );
  }

  return (
    <div className={shell.featureContainer}>
      <div className={shell.header}>
        <button className={shell.backButton}
                onClick={() => navigate('/dashboard/seller-intelligence')}>
          <FiArrowLeft /> Back
        </button>
        <div className={shell.titleSection}>
          <div className={shell.iconTitle}><h1>CTR Optimizer</h1></div>
          <p>Which listings lose clicks at the search result, why, and whether your fixes worked</p>
        </div>
      </div>
      <div className={shell.content}>{body()}</div>
    </div>
  );
}
